import {
  Typography,
  Box,
  TextField,
  Button,
  Pagination,
  PaginationItem,
  CircularProgress,
} from "@mui/material";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";

import type { Frame } from "@/types/dragon-tiles";

import {
  dragonTilesQueryOptions,
  framesPageQueryOptions,
} from "@/api/dragon-tiles/index";
import FrameCard from "@/components/FrameCard/FrameCard";
import { resolveActiveInnerTile } from "@/features/DragonTiles/AnalogSequencing/useSelectedInnerTile";
import { CreateFrameModal } from "@/features/DragonTiles/components/CreateFrameModal";
import {
  OuterWrapper,
  TopBarWrapper,
  TopBar,
  Title,
} from "@/features/DragonTiles/components/FrameEditPanel.styles";
import ScrollPanel from "@/features/DragonTiles/components/ScrollPanel";
import { useBulkFrameVariantPreviews } from "@/hooks/useBulkFrameVariantPreviews";
import {
  AddIcon,
  FirstPageIcon,
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
  LastPageIcon,
  SearchIcon,
} from "@/icons";
import { getVariantPreviewsForFrame } from "@/utils/frameVariantPreviews";

const PAGE_SIZE = 24;

interface BroadcastV2FrameSelectionPanelProps {
  siteId: string;
  selectedFrameId: string | null;
  onFrameSelect: (frame: Frame) => void;
}

export function BroadcastV2FrameSelectionPanel({
  siteId,
  selectedFrameId,
  onFrameSelect,
}: BroadcastV2FrameSelectionPanelProps) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isCreateFrameModalOpen, setIsCreateFrameModalOpen] = useState(false);

  const { data: dragonTiles } = useSuspenseQuery(
    dragonTilesQueryOptions(siteId),
  );
  const broadcastTile = dragonTiles?.find((tile) => tile.name === "Broadcast");

  const { data: framesResponse, isPending: isFramesPending } = useQuery(
    framesPageQueryOptions(siteId, page, PAGE_SIZE, {
      dragon_tile: broadcastTile?.id ?? "",
      medium: "email",
      order: "desc",
      order_by: "modify_date",
      ...(search.trim().length ? { search: search.trim() } : {}),
    }),
  );

  const frames = useMemo(() => framesResponse?.results ?? [], [framesResponse]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((framesResponse?.count ?? 0) / PAGE_SIZE)),
    [framesResponse],
  );

  const emailFrames = useMemo(
    () =>
      frames.filter((frame) =>
        frame.frame_variants.some((v) => v.medium === "email"),
      ),
    [frames],
  );

  const { bulkPreviews } = useBulkFrameVariantPreviews(siteId, emailFrames);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleFrameSelect = (frame: Frame) => {
    onFrameSelect(frame);
  };

  return (
    <>
      <OuterWrapper
        initial={{ x: 300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 300, opacity: 0 }}
        transition={{ type: "tween", duration: 0.3 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: (theme) =>
            theme.palette.background["paper-elevation-0"],
        }}
      >
        <TopBarWrapper>
          <TopBar>
            <Title flexGrow={1}>Select a Tile</Title>
            <Button
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateFrameModalOpen(true)}
            >
              Create New Tile
            </Button>
            <TextField
              placeholder="Search"
              value={search}
              size="small"
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <SearchIcon sx={{ mr: 2, color: "text.secondary" }} />
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </TopBar>
        </TopBarWrapper>

        <ScrollPanel>
          {isFramesPending ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 5,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                {emailFrames.map((frame) => {
                  const frameVariantPreviews = getVariantPreviewsForFrame(
                    frame,
                    bulkPreviews,
                  );

                  const isSelected = frame.id === selectedFrameId;

                  return (
                    <Box
                      key={frame.id}
                      sx={{
                        cursor: "pointer",
                        outline: isSelected ? "3px solid" : "none",
                        outlineColor: isSelected
                          ? "primary.main"
                          : "transparent",
                        borderRadius: 2,
                      }}
                    >
                      <FrameCard
                        frame={frame}
                        siteId={siteId}
                        onClick={handleFrameSelect}
                        isActionable={false}
                        variantPreviews={frameVariantPreviews}
                      />
                    </Box>
                  );
                })}
              </Box>

              {emailFrames.length === 0 && search.trim() && (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography variant="body1" color="text.secondary">
                    No tiles found matching &quot;{search}&quot;
                  </Typography>
                </Box>
              )}

              {emailFrames.length === 0 && !search.trim() && (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography variant="body1" color="text.secondary">
                    No email tiles available. Create one to get started.
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  py: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Pagination
                  page={page}
                  count={totalPages}
                  onChange={(_, newPage) => setPage(newPage)}
                  renderItem={(item) => (
                    <PaginationItem
                      slots={{
                        first: FirstPageIcon,
                        last: LastPageIcon,
                        previous: KeyboardArrowLeftIcon,
                        next: KeyboardArrowRightIcon,
                      }}
                      {...item}
                    />
                  )}
                />
              </Box>
            </>
          )}
        </ScrollPanel>
      </OuterWrapper>

      <CreateFrameModal
        open={isCreateFrameModalOpen}
        onClose={() => setIsCreateFrameModalOpen(false)}
        siteId={siteId}
        parentTileId={broadcastTile?.id || ""}
        innerTileId={
          broadcastTile
            ? (resolveActiveInnerTile(broadcastTile, null)?.id ?? "")
            : ""
        }
        forcedLayoutType="email"
        returnTo="broadcast-v2"
      />
    </>
  );
}
